import { useState, useEffect, useMemo } from 'react';
import { Package, ChevronsDownUp, ChevronsUpDown, X } from 'lucide-react';
import RecipeCategoryTree from '../receptura/RecipeCategoryTree';
import SeznamPanel from './SeznamPanel';
import { SECTION_COLOR } from '../receptura/receptUtils';
import { useVyberPolozek, pocetPolozek } from '../receptura/useVyberPolozek';
import MaterialListRow from './MaterialListRow';

const BEZ_KAT = '__bez__';

const collectCatIds = (nodes, acc = []) => {
    for (const n of nodes || []) {
        acc.push(n.id);
        if (n.children?.length) collectCatIds(n.children, acc);
    }
    return acc;
};

/**
 * Levý panel Spižírny (suroviny / obaly / spotřební zboží).
 *
 * Kostra i ovládání jsou schválně shodné s receptářem — stromová navigace
 * kategorií, hledání, hromadný výběr přes Ctrl a přetahování do kategorií.
 * Liší se obsah řádku a to, co se hlídá: u materiálu nejde o recepturu, ale
 * o cenu a alergeny, takže „semafor" se počítá tady na klientovi.
 */
export default function MaterialListPanel({
    typ, labels,
    isMobile, isDark, mutedColor, secondaryColor, cardStyle, inputStyle,
    search, setSearch, katStrom, items, loading,
    activeId, onSelect, onNew,
    onCatSave, onCatAdd, onCatDelete, onMoveItems, onReparentCat,
    // Sloučený seznam obalů a spotřebního zboží — řádek pak nese odznak druhu.
    sDruhem = false,
}) {
    const searchActive = !!search.trim();
    const sAlergeny = typ === 'MAT';

    const [expanded, setExpanded] = useState(() => new Set());
    useEffect(() => { setExpanded(new Set()); }, [typ]);

    const vyber = useVyberPolozek(typ);

    // Semafor položky — obdoba validace receptu, jen s pravidly, která dávají
    // smysl u materiálu. Strom si z `validace` sám poskládá počty u kategorií.
    const polozky = useMemo(() => (items || []).map(it => {
        const chyby = [];
        const varovani = [];
        if (it.aktualni_cena == null) chyby.push('Chybí platná cena');
        if (sAlergeny && !it.bez_alergenu && !(it.alergeny || []).length) varovani.push('Alergeny nevyplněny');
        const stav = chyby.length ? 'red' : (varovani.length ? 'orange' : null);
        return { ...it, validace: { stav, chyby, varovani, tipy: [] } };
    }), [items, sAlergeny]);

    const filtrovane = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return polozky;
        return polozky.filter(it => it.nazev?.toLowerCase().includes(q) || it.kod?.toLowerCase().includes(q));
    }, [polozky, search]);

    const allCatIds = useMemo(() => [...collectCatIds(katStrom), BEZ_KAT], [katStrom]);
    const allExpanded = allCatIds.length > 0 && allCatIds.every(id => expanded.has(id));
    const hasTree = allCatIds.length > 1;
    const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(allCatIds));

    const btnPrimary = {
        background: SECTION_COLOR, color: '#fff', border: 'none', borderRadius: 6,
        padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%',
    };

    const filtry = (
        <>
            {/* Vybráno N — hromadný přesun přetažením do kategorie */}
            {vyber.vybrane.size > 0 && (
                <div className="flex items-center gap-2 rounded-lg px-2 py-1.5"
                    style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.30)' }}>
                    <span className="text-[11px] font-semibold" style={{ color: '#3B82F6' }}>
                        {pocetPolozek(vyber.vybrane.size)}
                    </span>
                    <span className="text-[11px] flex-1 truncate" style={{ color: mutedColor }}>přetáhni do kategorie</span>
                    <button onClick={vyber.zrus} title="Zrušit výběr (Esc)"
                        className="flex items-center justify-center rounded-md"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 2, flexShrink: 0 }}>
                        <X size={13} />
                    </button>
                </div>
            )}

            {hasTree && !searchActive && (
                <div className="flex justify-end">
                    <button onClick={toggleAll}
                        title={allExpanded ? 'Sbalit všechny kategorie' : 'Rozbalit všechny kategorie'}
                        className="flex items-center gap-1 text-[11px] font-medium rounded-md"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: '2px 4px' }}
                        onMouseEnter={e => e.currentTarget.style.color = SECTION_COLOR}
                        onMouseLeave={e => e.currentTarget.style.color = mutedColor}>
                        {allExpanded ? <ChevronsDownUp size={13} /> : <ChevronsUpDown size={13} />}
                        {allExpanded ? 'Sbalit vše' : 'Rozbalit vše'}
                    </button>
                </div>
            )}
        </>
    );

    return (
        <SeznamPanel
            isMobile={isMobile} isDark={isDark} mutedColor={mutedColor}
            cardStyle={cardStyle} inputStyle={inputStyle} akcent={SECTION_COLOR}
            search={search} setSearch={setSearch}
            hledatPlaceholder={`Hledat ${labels.jednotne} / kód…`}
            novyTitle={labels.novy} onNew={onNew}
            filtry={filtry}
            loading={loading}
            prazdne={filtrovane.length === 0 ? { Icon: Package, text: `Zatím žádné ${labels.mnozne}`, akce: 'Vytvořit první' } : null}
            prazdneHledani={searchActive}
        >
            <RecipeCategoryTree
                katStrom={katStrom} items={filtrovane}
                activeId={activeId} onSelect={onSelect}
                searchActive={searchActive}
                isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor}
                expanded={expanded} setExpanded={setExpanded}
                vybrane={vyber.vybrane} onKlikRadek={vyber.klikNaRadek}
                zobrazitViditelnost={false}
                renderRow={({ item, active, selected, depthPad, onClick, onDragStartItem }) => (
                    <MaterialListRow key={item.id} item={item} active={active} selected={selected}
                        depthPad={depthPad} onClick={onClick} onDragStartItem={onDragStartItem}
                        isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor}
                        sAlergeny={sAlergeny} druh={sDruhem ? item._druh : null} />
                )}
                onCatSave={onCatSave} onCatAdd={onCatAdd} onCatDelete={onCatDelete}
                onMoveItems={(ids, catId) => { onMoveItems?.(ids, catId); vyber.zrus(); }}
                onReparentCat={onReparentCat} />
        </SeznamPanel>
    );
}
